import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Car, Plus, Loader2, Info, Check } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const DefaultVehicleMappingManagement = ({ defaultStore }) => {
  const [vehicleMappings, setVehicleMappings] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    makeDesc: '',
    modelDesc: ''
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedStore, setSelectedStore] = useState(defaultStore || '');
  const [isInjecting, setIsInjecting] = useState(false);

  const isAdmin = authService.isAdmin();
  const isSuperAdmin = authService.isSuperAdmin();

  useEffect(() => {
    fetchVehicleMappings();
    fetchStores();
  }, []);

  const fetchVehicleMappings = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-vehicle-mappings`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setVehicleMappings(data);
      } else {
        setError('Failed to fetch default vehicle mappings');
      }
    } catch (err) {
      setError('Error fetching default vehicle mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const url = editingMapping
        ? `${getApiBaseUrl()}/admin/default-vehicle-mappings/${editingMapping.id}`
        : `${getApiBaseUrl()}/admin/default-vehicle-mappings`;

      const method = editingMapping ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchVehicleMappings();
        handleCloseDialog();
        setSuccess(editingMapping ? 'Vehicle mapping updated successfully' : 'Vehicle mapping created successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save vehicle mapping');
      }
    } catch (err) {
      setError('Error saving vehicle mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (mapping) => {
    setError('');
    setSuccess('');
    setEditingMapping(mapping);
    setFormData({
      make: mapping.make,
      model: mapping.model,
      makeDesc: mapping.makeDesc || '',
      modelDesc: mapping.modelDesc || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    const mapping = vehicleMappings.find((m) => m.id === id);
    const label = mapping ? `"${[mapping.make, mapping.model].filter(Boolean).join(' ')}"` : 'this vehicle mapping';
    const confirmed = await confirm({
      title: `Delete vehicle mapping ${label}?`,
      message: `The default vehicle mapping ${label} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-vehicle-mappings/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        await fetchVehicleMappings();
        setSuccess('Vehicle mapping deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete vehicle mapping');
      }
    } catch (err) {
      setError('Error deleting vehicle mapping');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMapping(null);
    setFormData({
      make: '',
      model: '',
      makeDesc: '',
      modelDesc: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSelectAll = () => {
    if (selectedIds.length === vehicleMappings.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(vehicleMappings.map((mapping) => mapping.id));
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleInject = async () => {
    if (!selectedStore) {
      setError('Please select a store first');
      return;
    }
    if (selectedIds.length === 0) {
      setError('Please select at least one default vehicle mapping to inject');
      return;
    }
    setIsInjecting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/default-vehicle-mappings/inject-to-store`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          storeId: selectedStore,
          defaultVehicleMappingIds: selectedIds
        })
      });
      if (response.ok) {
        setSelectedIds([]);
        const count = selectedIds.length;
        setSuccess(`${count} default vehicle mapping${count !== 1 ? 's' : ''} injected to store successfully`);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to inject default vehicle mappings');
      }
    } catch (err) {
      setError('Error injecting default vehicle mappings');
    } finally {
      setIsInjecting(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Default Vehicle Data"
        description="Manage default vehicle data used to bulk populate a new store"
      />

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800 leading-relaxed">
          This is a reusable catalog of vehicle make/model mappings. Select a store and the mappings you want to copy, then click <strong>Inject Selected</strong> to add them to that store's vehicle mapping list.
        </div>
      </div>

      {success && (
        <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Store Selection and Inject Section */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Store for Injection
              </label>
              <select
                value={selectedStore || ''}
                onChange={(e) => setSelectedStore(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select a store</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name || store.storeId}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleInject}
                disabled={isInjecting || !selectedStore || selectedIds.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition"
              >
                {isInjecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus size={16} />
                )}
                {isInjecting ? 'Injecting...' : 'Inject Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Mappings List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center gap-3">
          <div className="flex items-center gap-4">
            <h3 className="text-sm font-medium text-gray-900">
              Default Vehicle Data ({vehicleMappings.length})
            </h3>
            {isAdmin && vehicleMappings.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                {selectedIds.length === vehicleMappings.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <AdminAddButton onClick={() => {
                setSuccess('');
                setError('');
                setOpenDialog(true);
              }} />
            )}
          </div>
        </div>

        {loading ? (
          <AdminLoading />
        ) : vehicleMappings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Car className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">No default vehicle data configured yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-medium border-b border-gray-200">
                <tr>
                  {isAdmin && (
                    <th className="p-4 w-10">
                      <span className="sr-only">Select</span>
                    </th>
                  )}
                  <th className="p-4">Make</th>
                  <th className="p-4">Make Description</th>
                  <th className="p-4">Model</th>
                  <th className="p-4">Model Description</th>
                  {isSuperAdmin && <th className="p-4 w-24 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {vehicleMappings.map((mapping) => (
                  <tr key={mapping.id} className="hover:bg-gray-50 transition">
                    {isAdmin && (
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(mapping.id)}
                          onChange={() => handleSelect(mapping.id)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                      </td>
                    )}
                    <td className="p-4 font-medium text-gray-900">{mapping.make}</td>
                    <td className="p-4 text-gray-600">{mapping.makeDesc || '-'}</td>
                    <td className="p-4 font-medium text-gray-900">{mapping.model}</td>
                    <td className="p-4 text-gray-600">{mapping.modelDesc || '-'}</td>
                    {isSuperAdmin && (
                      <td className="p-4">
                        <AdminRowActions>
                          <AdminActionButton variant="edit" onClick={() => handleEdit(mapping)} />
                          <AdminActionButton variant="delete" loading={deletingId === mapping.id} loadingLabel="Deleting…" onClick={() => handleDelete(mapping.id)} />
                        </AdminRowActions>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingMapping ? 'Edit Vehicle Mapping' : 'Create Vehicle Mapping'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make *
                </label>
                <input
                  type="text"
                  required
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Toyota"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make Description
                </label>
                <input
                  type="text"
                  value={formData.makeDesc}
                  onChange={(e) => setFormData({ ...formData, makeDesc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Toyota Motor Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model *
                </label>
                <input
                  type="text"
                  required
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Camry"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Description
                </label>
                <input
                  type="text"
                  value={formData.modelDesc}
                  onChange={(e) => setFormData({ ...formData, modelDesc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Camry LE Sedan"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingMapping ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {confirmDialog}
    </div>
  );
};

export default DefaultVehicleMappingManagement;
